import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, AlertCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", data: null, attempts: 0 });

  const poll = useCallback(async (attempts) => {
    if (!sessionId) return setState({ status: "missing", data: null, attempts });
    if (attempts >= 10) return setState((s) => ({ ...s, status: "timeout" }));
    try {
      const { data } = await api.get(`/payments/v1/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") return setState({ status: "paid", data, attempts });
      if (data.status === "expired") return setState({ status: "expired", data, attempts });
      setTimeout(() => poll(attempts + 1), 2000);
    } catch {
      setState({ status: "error", data: null, attempts });
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="bg-[#FDFBF7] min-h-screen">
      <Navbar />
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="checkout-success-page">
        {state.status === "polling" && (
          <div>
            <p className="label-eyebrow">Processing</p>
            <h1 className="font-display text-4xl md:text-5xl mt-4">Confirming your payment…</h1>
            <p className="mt-4 text-[#5C5C5C]">Hang tight — this usually takes a few seconds.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div>
            <div className="w-14 h-14 rounded-full bg-[#C5A059] text-white inline-flex items-center justify-center"><Check size={24}/></div>
            <p className="label-eyebrow mt-6">Payment received</p>
            <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Welcome aboard. We're on it.</h1>
            <p className="mt-6 text-[#5C5C5C] leading-relaxed text-lg">
              We'll reach out within one business day to kick off discovery and set up your customer portal. Receipt and project details will arrive at the email on your payment.
            </p>
            <div className="mt-8 flex gap-3">
              <Link to="/" className="btn-primary" data-testid="cs-home">Back to home</Link>
              <Link to="/portal/login" className="btn-secondary" data-testid="cs-portal">Open client portal</Link>
            </div>
          </div>
        )}
        {state.status === "expired" && (
          <div>
            <AlertCircle className="text-[#C5A059]" size={28}/>
            <h1 className="font-display text-4xl mt-4">Checkout expired</h1>
            <p className="mt-3 text-[#5C5C5C]">No charge was made. <Link to="/pricing" className="ulink">Try again →</Link></p>
          </div>
        )}
        {(state.status === "error" || state.status === "missing" || state.status === "timeout") && (
          <div>
            <AlertCircle className="text-[#C5A059]" size={28}/>
            <h1 className="font-display text-4xl mt-4">We couldn't verify your payment.</h1>
            <p className="mt-3 text-[#5C5C5C]">If you were charged, email hello@shumwaytech.com — we'll sort it instantly.</p>
            <Link to="/pricing" className="btn-secondary mt-6 inline-flex">Back to pricing</Link>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
